import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {SMCSelect, SMCOption} from '../../../components/Simple/SMCSelect';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import {SMCCheckbox} from '../../../components/Simple/SMCCheckbox';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import EmptyRow from '../../../components/Simple/EmptyRow';
import SMCButton from '../../../components/Simple/SMCButton';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import generateIcon from '../../../assets/images/generate.png';
import RowLayout from '../../../components/Layouts/RowLayout';
import SetUserModal from '../../PopupPages/SetUserModal';
import {useBoolean} from '../../../utils/hooks';
import UserWizard from '../UserWizard';
import Api from '../../../api/SMPFetchHelper';
import loadingSpinner from '../../../assets/images/loading_spinner.gif';
import {ObjectTypes} from '../../../api/consts';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import GatewayWizard, {GatewayTypeValues} from './index';
import SetGatewayModal from '../../PopupPages/SetGatewayModal';
import {generateRandomString} from '../../../utils/utils';

function GatewayWizardBody({
    onCancel,
    onFinish,
    wizardVars
}) {
    const { t } = useTranslation();

    const [isSetUserModalOpen, openSetUserModal, closeSetUserModal] = useBoolean(false);
    const [isUserWizardOpen, openUserWizard, closeUserWizard] = useBoolean(false);
    
    const [isSetGatewayModalOpen, openSetGatewayModal, closeSetGatewayModal] = useBoolean(false);
    const [isGatewayWizardOpen, openGatewayWizard, closeGatewayWizard] = useBoolean(false);
    const [currentEditedMemberNumber, setCurrentEditedMemberNumber] = useState(false);

    const [isFetchingName, setIsFetchingName] = useState(false);

    const {
        gatewayType, setGatewayType,
        name, setName,
        description, setDescription,
        managedBySMP, setManagedBySMP,
        staticIP, setStaticIP,
        planId, setPlanId,
        registrationKey, setRegistrationKey,
        ownerId, setOwnerId,
        member1Name, setMember1Name,
        member2Name, setMember2Name,
        isHaMember,
        availablePlans
    } = wizardVars;

    const generateGatewayName = useCallback(() => {
        setIsFetchingName(true);
        Api.doAction(ObjectTypes.GATEWAY, 'generate-gateway-name')
        .then(resp => {
            setName(resp.data.name);
        })
        // .catch(err => {})
        .finally(() => {
            setIsFetchingName(false);
        });
    }, [setName]);


    return (
        <>
            <SMCModalBody>
                <SMCSelect
                    value={gatewayType}
                    onChange={e => setGatewayType(e.target.value)}
                    label={t('SMC_GATEWAY_MANAGEMENT_TYPE')}
                >
                    <SMCOption value={GatewayTypeValues.smallOfficeAppliance}>Small Office Appliance</SMCOption>
                    {/* If new gateway is HA member, it can't be of type HA gateway */}
                    {
                        !isHaMember && (
                            <SMCOption value={GatewayTypeValues.smallOfficeHA}>Small Office HA</SMCOption>
                        )
                    }
                </SMCSelect>
                {/* Name */}
                <RowLayout>
                    <SMCTextInput
                        label={t('SMC_GATEWAY_NAME')}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        maxLength={20}
                    />
                    <SMCButton onClick={() => generateGatewayName()}>
                        <img src={generateIcon} alt="generate" />
                        {t('SMC_GEN_GENERATE')}
                    </SMCButton>
                    { isFetchingName && <img src={loadingSpinner} alt="" height="32px" /> }
                </RowLayout>

                {/* Description */}
                <SMCTextInput
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    label={t("SMC_GATEWAY_DESCRIPTION")}
                />
                {/* Managed by SMP */}
                {
                    gatewayType === GatewayTypeValues.smallOfficeAppliance &&
	                <SMCCheckbox
	                    checked={managedBySMP}
	                    onChange={e => setManagedBySMP(e.target.checked)}
	                    label={t("SMP.GATEWAYS.PAGE.GENERAL.MANAGED_BY_SMP")}
	                />
                }
                {
                    (gatewayType ===GatewayTypeValues.smallOfficeHA) &&
                    <EmptyRow/>
                }
                {/* Static IP, show only if not managed by SMP */}
                {
                    (!managedBySMP && gatewayType === GatewayTypeValues.smallOfficeAppliance) &&
                    <>
                        <SMCTextInput
                            value={staticIP}
                            onChange={e => setStaticIP(e.target.value)}
                            label={t("SMC_GATEWAY_IP")}
                        />
                        <EmptyRow />
                    </>
                }
                {/* Plan id, show only if managed by SMP */}
                {
                    (managedBySMP || gatewayType === GatewayTypeValues.smallOfficeHA) && (
                        <SMCSelect
                            value={planId}
                            onChange={e => setPlanId(e.target.value)}
                            label={t("SMP.GATEWAYS.PAGE.GENERAL.PLAN")}
                            tooltip={t("SMC_GATEWAYWIZ_CREATE_NEW_GATEWAY_REMOTE_MANAGEMENT.TOOLTIP")}
                        >
                            {
                                availablePlans && availablePlans.map(planName => (
                                    <SMCOption key={planName} value={planName}>{planName}</SMCOption>
                                ))
                            }
                        </SMCSelect>
                    )
                }
                {/* Registration key, show only if managed by SMP and type is Small Office Appliance */}
                {
                    managedBySMP && gatewayType === GatewayTypeValues.smallOfficeAppliance && (
                        <RowLayout>
                            <SMCTextInput
                                value={registrationKey}
                                onChange={e => setRegistrationKey(e.target.value)}
                                label={t("SMP.GATEWAYS.PAGE.GENERAL.REG_KEY")}
                            />
                            <SMCButton onClick={() => setRegistrationKey(generateRandomString(8))}>
                                <img src={generateIcon} alt="Generate" />
                                {t('SMC_GEN_GENERATE')}
                            </SMCButton>
                        </RowLayout>
                    )
                }
                <EmptyRow />
                {/* Owner ID */}
                <RowLayout spacing={2}>
                    <SMCTextInput
                        value={ownerId}
                        onChange={e => setOwnerId(e.target.value)}
                        label={t("SMC_GATEWAYS_OWNER_ID")}
                    />
                    <SMCButton icon="filter" onClick={openSetUserModal}>
                        {t('SMC_GEN_SEARCH')}...
                    </SMCButton>
                    <SMCButton icon="new" onClick={openUserWizard}>
                        {t('SMC_GEN_NEW')}...
                    </SMCButton>
                </RowLayout>
                {/* HA member 1, only show if gateway is of type small office HA */}
                {
                    gatewayType === GatewayTypeValues.smallOfficeHA && (
                        <RowLayout spacing={2}>
                            <SMCTextInput
                                value={member1Name}
                                onChange={e => setMember1Name(e.target.value)}
                                label={"Member 1"}
                            />
                            <SMCButton
                                icon="filter"
                                onClick={() => {
                                    setCurrentEditedMemberNumber(1);
                                    openSetGatewayModal();
                                }}
                            >
                                {t('SMC_GEN_SEARCH')}...
                            </SMCButton>
                            <SMCButton
                                icon="new"
                                onClick={() => {
                                    setCurrentEditedMemberNumber(1);
                                    openGatewayWizard();
                                }}>
                                {t('SMC_GEN_NEW')}...
                            </SMCButton>
                        </RowLayout>
                    )
                }
                {/* HA member 2, only show if gateway is of type small office HA */}
                {
                    gatewayType === GatewayTypeValues.smallOfficeHA && (
                        <RowLayout spacing={2}>
                            <SMCTextInput
                                value={member2Name}
                                onChange={e => setMember2Name(e.target.value)}
                                label={"Member 2"}
                            />
                            <SMCButton
                                icon="filter"
                                onClick={() => {
                                    setCurrentEditedMemberNumber(2);
                                    openSetGatewayModal();
                                }}
                            >
                                {t('SMC_GEN_SEARCH')}...
                            </SMCButton>
                            <SMCButton
                                icon="new"
                                onClick={() => {
                                    setCurrentEditedMemberNumber(2);
                                    openGatewayWizard();
                                }}>
                                {t('SMC_GEN_NEW')}...
                            </SMCButton>
                        </RowLayout>
                    )
                }
                {/* Popup windows */}
                {/* User windows */}
                <SetUserModal
                    isOpen={isSetUserModalOpen}
                    onRequestClose={closeSetUserModal}
                    selectMultiple={false}
                    onSuccess={userNameList => {
                        setOwnerId(userNameList[0] || '');
                        closeSetUserModal();
                    }}
                />
                <UserWizard
                    isOpen={isUserWizardOpen}
                    onRequestClose={closeUserWizard}
                    onSuccess={userName => {
                        setOwnerId(userName);
                        closeUserWizard();
                    }}
                />
                {/* Gateway windows */}
                <SetGatewayModal
                    isOpen={isSetGatewayModalOpen}
                    onRequestClose={closeSetGatewayModal}
                    selectMultiple={false}
                    onSuccess={gatewayNameList => {
                        if (currentEditedMemberNumber === 1) {
                            setMember1Name(gatewayNameList[0] || '');
                        } else if (currentEditedMemberNumber === 2) {
                            setMember2Name(gatewayNameList[0] || '');
                        }
                        closeSetGatewayModal();
                    }}
                />
                <GatewayWizard
                    isOpen={isGatewayWizardOpen}
                    onRequestClose={closeGatewayWizard}
                    isHaMember={true}
                    onSuccess={gatewayName => {
                        if (currentEditedMemberNumber === 1) {
                            setMember1Name(gatewayName || '');
                        } else if (currentEditedMemberNumber === 2) {
                            setMember2Name(gatewayName || '');
                        }
                        closeGatewayWizard();
                    }}
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={onFinish} style={{ marginRight: 5 }}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_FINISH')}
                </SMCButton>
                <SMCButton onClick={onCancel}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </>
    );
}

export default GatewayWizardBody;